import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Link,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { questionnaireService } from '../services/questionnaireService';
import { QuestionnaireType } from '../types/questionnaire.types';
import { RowContextMenu, ContextMenuOption } from '../components/ui/RowContextMenu';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import TablePagination from '../components/table/TablePagination';
import useTablePage from '../components/table/hooks/useTablePage';
import { questionnaireColumns, generateDynamicAnswerColumns, generateDynamicScoreColumns } from '../config/questionnaireColumns';
import { formatDateSafe as formatDate, formatDateTimeSafe as formatDateTime } from '../utils/dateUtils';
import QuestionnaireEmailSendDialog from '../components/dialogs/QuestionnaireEmailSendDialog';
import QuestionnaireSMSSendDialog from '../components/dialogs/QuestionnaireSMSSendDialog';
import { ViewQuestionnaireModal } from '../components/modals/ViewQuestionnaireModal';
import { Visibility as VisibilityIcon } from '@mui/icons-material';

// Interface for questionnaire list items with answers
interface QuestionnaireListItem {
  id: number;
  patient_id: number | null;
  questionnaire_type_id: number | null;
  tenant_id: number;
  status: string;
  sent_date: string | null;
  start_date: string | null;
  finish_date: string | null;
  token?: string | null;
  created_at: string;
  updated_at: string;
  questionnaire_type?: {
    id: number;
    name: string;
    title: string;
    path?: string;
  } | null;
  patient_name?: string | null;
  referral_id?: number | null;
  referral_name?: string | null;
  answers?: Record<string, string[]>;
  scores?: Record<string, string> | null;
  last_exit?: string | null;
  exit_location?: string | null;
  last_section?: string | null;
  last_section_index?: number | null;
}

const QuestionnairesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentTenant, refreshKey } = useTenant();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Pagination
  const PAGE_SIZE = 100;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const showPagination = totalCount > PAGE_SIZE;

  const [searchInputValue, setSearchInputValue] = useState('');

  // Filter state
  const [questionnaireTypes, setQuestionnaireTypes] = useState<QuestionnaireType[]>([]);
  const [selectedTypeId, setSelectedTypeId] = useState<number | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [answerFields, setAnswerFields] = useState<string[]>([]);
  const [scoreNames, setScoreNames] = useState<string[]>([]);

  // Email dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedForEmail, setSelectedForEmail] = useState<QuestionnaireListItem | null>(null);

  // SMS dialog state
  const [smsSendDialogOpen, setSmsSendDialogOpen] = useState(false);
  const [selectedForSms, setSelectedForSms] = useState<QuestionnaireListItem | null>(null);

  // View questionnaire modal state
  const [viewQuestionnaireModalOpen, setViewQuestionnaireModalOpen] = useState(false);
  const [selectedForView, setSelectedForView] = useState<QuestionnaireListItem | null>(null);

  // Extract unique answer field names from data
  const extractAnswerFields = useCallback((data: QuestionnaireListItem[]): string[] => {
    const fields = new Set<string>();
    data.forEach(item => {
      if (item.answers) {
        Object.keys(item.answers).forEach(key => fields.add(key));
      }
    });
    return Array.from(fields).sort();
  }, []);

  // Extract unique score names from data
  const extractScoreNames = useCallback((data: QuestionnaireListItem[]): string[] => {
    const names = new Set<string>();
    data.forEach(item => {
      if (item.scores) {
        Object.keys(item.scores).forEach(key => names.add(key));
      }
    });
    return Array.from(names).sort();
  }, []);

  const getSortValue = useCallback((item: QuestionnaireListItem, key: string): any => {
    if (key.startsWith('answer_')) {
      const fieldName = key.replace('answer_', '');
      const values = item.answers?.[fieldName];
      return values ? values.join(', ').toLowerCase() : '';
    }

    if (key.startsWith('score_')) {
      const scoreName = key.replace('score_', '');
      const scoreValue = item.scores?.[scoreName];
      return scoreValue?.toLowerCase() || '';
    }

    switch (key) {
      case 'type': return item.questionnaire_type?.name?.toLowerCase() || '';
      case 'patientName': return item.patient_name?.toLowerCase() || '';
      case 'referralName': return item.referral_name?.toLowerCase() || '';
      case 'status': return item.status.toLowerCase();
      case 'sentDate': return item.sent_date || '';
      case 'startDate': return item.start_date || '';
      case 'finishDate': return item.finish_date || '';
      case 'createdAt': return item.created_at || '';
      case 'lastExit': return item.last_exit || '';
      case 'exitLocation': return (item.exit_location || '').toLowerCase();
      case 'lastSection': return item.last_section_index ?? -1;
      default: return '';
    }
  }, []);

  // Combine base columns with dynamic answer columns and score columns
  const allColumns = useMemo(() => {
    return [
      ...questionnaireColumns,
      ...generateDynamicAnswerColumns(answerFields),
      ...generateDynamicScoreColumns(scoreNames)
    ];
  }, [answerFields, scoreNames]);

  // Filter state object for saving to views
  const filters = useMemo(() => ({
    questionnaire_type_id: selectedTypeId,
    status: statusFilter,
  }), [selectedTypeId, statusFilter]);

  // Handler to apply filters when loading a view
  const handleFiltersChange = useCallback((newFilters: Record<string, any>) => {
    if (newFilters.questionnaire_type_id !== undefined) {
      setSelectedTypeId(newFilters.questionnaire_type_id);
    }
    if (newFilters.status !== undefined) {
      setStatusFilter(newFilters.status);
    }
  }, []);

  const table = useTablePage<QuestionnaireListItem>({
    entityType: 'questionnaires',
    columns: allColumns,
    tenantId: currentTenant?.id,
    getSortValue,
    filters,
    onFiltersChange: handleFiltersChange,
  });


  // Load questionnaire types
  useEffect(() => {
    const loadQuestionnaireTypes = async () => {
      if (!currentTenant) {
        setQuestionnaireTypes([]);
        return;
      }

      try {
        const types = await questionnaireService.getQuestionnaireTypes(currentTenant.id);
        setQuestionnaireTypes(types);
      } catch (err) {
        console.error('Error loading questionnaire types:', err);
        toast.error('Failed to load questionnaire types');
        setQuestionnaireTypes([]);
      }
    };

    loadQuestionnaireTypes();
  }, [currentTenant]);

  // Reset to page 1 when filters, sort, or search change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTypeId, statusFilter, table.sortBy, table.sortOrder, table.searchQuery]);

  // Load questionnaires data
  useEffect(() => {
    const loadData = async () => {
      if (!user || !currentTenant) {
        table.setLoading(false);
        return;
      }

      try {
        table.setLoading(true);
        const skip = (currentPage - 1) * PAGE_SIZE;
        const result = await questionnaireService.getPatientQuestionnaires(
          currentTenant.id,
          undefined, // patientId
          statusFilter || undefined,
          selectedTypeId,
          skip,
          PAGE_SIZE,
          table.sortBy ?? undefined,
          table.sortOrder,
          table.searchQuery || undefined,
        );

        const dataWithAnswers = result.items as unknown as QuestionnaireListItem[];
        setTotalCount(result.total);

        // Extract answer fields from data
        const fields = extractAnswerFields(dataWithAnswers);
        setAnswerFields(fields);

        // Extract score names from data
        const scores = extractScoreNames(dataWithAnswers);
        setScoreNames(scores);

        table.setData(dataWithAnswers);
        table.setError(null);
      } catch (err) {
        console.error('Error loading questionnaires:', err);
        table.setError(err instanceof Error ? err.message : 'Unknown error');
        toast.error('Failed to load questionnaires');
      } finally {
        table.setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentTenant, refreshKey, selectedTypeId, statusFilter, currentPage, table.sortBy, table.sortOrder, table.searchQuery]);

  const handleViewDetails = () => {
    if (table.selectedItem) {
      // Navigate to patient detail page if patient_id exists
      if (table.selectedItem.patient_id) {
        navigate(`/patients/${table.selectedItem.patient_id}`);
      }
    }
    table.handleMenuClose();
  };

  const handleSendEmail = () => {
    if (table.selectedItem) {
      setSelectedForEmail(table.selectedItem);
      setEmailDialogOpen(true);
    }
    table.handleMenuClose();
  };

  const handleSendSms = () => {
    if (table.selectedItem) {
      setSelectedForSms(table.selectedItem);
      setSmsSendDialogOpen(true);
    }
    table.handleMenuClose();
  };

  const handleViewQuestionnaire = () => {
    if (table.selectedItem) {
      setSelectedForView(table.selectedItem);
      setViewQuestionnaireModalOpen(true);
    }
    table.handleMenuClose();
  };

  const handleResendIlWebhook = async () => {
    const item = table.selectedItem;
    table.handleMenuClose();
    if (!item || !currentTenant) return;
    try {
      await questionnaireService.resendIlWebhook(item.id, currentTenant.id);
      toast.success('Webhook sent successfully');
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to send webhook');
    }
  };

  const handleEmailSuccess = (message: string) => {
    toast.success(message);
  };

  const handleEmailError = (error: string) => {
    toast.error(error);
  };

  const getContextMenuOptions = (): ContextMenuOption[] => {
    if (!table.selectedItem) return [];

    const options: ContextMenuOption[] = [];

    // Add View Questionnaire option for all statuses
    options.push({
      label: 'View Questionnaire',
      icon: <VisibilityIcon sx={{ mr: 2 }} />,
      onClick: handleViewQuestionnaire,
    });

    if (table.selectedItem.patient_id) {
      options.push({
        label: 'View Patient',
        icon: <AssignmentIcon sx={{ mr: 2 }} />,
        onClick: handleViewDetails,
      });
    }

    // Offer the sends when there is somewhere to send to: an answer that holds
    // a contact detail, or a linked patient whose record holds one.
    //
    // This used to require answers, which ruled out the case the sends are
    // most wanted for - a questionnaire that was issued and never filled in
    // has no answers at all, and chasing it from this table was impossible.
    const hasAnswers = !!table.selectedItem.answers
      && Object.keys(table.selectedItem.answers).length > 0;
    if (hasAnswers || table.selectedItem.patient_id) {
      options.push({
        label: 'Send Email',
        icon: <EmailIcon sx={{ mr: 2 }} />,
        onClick: handleSendEmail,
      });
      options.push({
        label: 'Send SMS',
        icon: <SmsIcon sx={{ mr: 2 }} />,
        onClick: handleSendSms,
      });
    }

    // Manual retry of the IL webhook for il_webhook questionnaires that were submitted
    const selectedItem = table.selectedItem;
    const selectedType = questionnaireTypes.find((t) => t.id === selectedItem.questionnaire_type_id);
    const isSubmitted = ['SUBMITTED', 'FINISHED'].includes(selectedItem.status.toUpperCase());
    if (selectedType?.type === 'il_webhook' && isSubmitted) {
      options.push({
        label: 'Resend Webhook',
        icon: <ReplayIcon sx={{ mr: 2 }} />,
        onClick: handleResendIlWebhook,
      });
    }

    return options;
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' => {
    switch (status.toLowerCase()) {
      case 'sent': return 'default';
      case 'start': return 'primary';
      case 'submitted': return 'success';
      case 'finished': return 'success';
      default: return 'default';
    }
  };

  // Column value renderer
  const getColumnValue = (item: QuestionnaireListItem, columnKey: string): React.ReactNode => {
    if (columnKey.startsWith('answer_')) {
      const fieldName = columnKey.replace('answer_', '');
      const values = item.answers?.[fieldName];
      return values && values.length > 0 ? values.join(', ') : '—';
    }

    if (columnKey.startsWith('score_')) {
      const scoreName = columnKey.replace('score_', '');
      return item.scores?.[scoreName] || '—';
    }

    switch (columnKey) {
      case 'type':
        return item.questionnaire_type?.name || '—';
      case 'patientName':
        return item.patient_name && item.patient_id ? (
          <Link
            component="button"
            variant="body2"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/patients/${item.patient_id}`);
            }}
            sx={{ textAlign: 'left' }}
          >
            {item.patient_name}
          </Link>
        ) : '—';
      case 'status':
        return (
          <Chip
            label={item.status}
            color={getStatusColor(item.status)}
            size="small"
          />
        );
      case 'referralName':
        return item.referral_name || '—';
      case 'sentDate':
        return formatDate(item.sent_date);
      case 'startDate':
        return formatDate(item.start_date);
      case 'finishDate':
        return formatDate(item.finish_date);
      case 'createdAt':
        return formatDateTime(item.created_at);
      case 'lastExit':
        return item.last_exit ? formatDateTime(item.last_exit) : '—';
      case 'exitLocation':
        return item.exit_location || '—';
      case 'lastSection':
        if (!item.last_section) return '—';
        return item.last_section_index != null
          ? `${item.last_section} (${item.last_section_index + 1})`
          : item.last_section;
      default:
        return '—';
    }
  };

  // Mobile card renderer
  const renderMobileCard = (item: QuestionnaireListItem) => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} sx={{ pr: 5 }}>
        <Box display="flex" alignItems="center" flex={1}>
          <AssignmentIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.5rem' }} />
          <Typography variant="h6" sx={{ fontWeight: 'medium', fontSize: '1.1rem' }}>
            {item.questionnaire_type?.name || 'Unknown Type'}
          </Typography>
        </Box>
        <Chip
          label={item.status}
          color={getStatusColor(item.status)}
          size="small"
        />
      </Box>
      <Box sx={{ pl: 4 }}>
        {item.patient_name && item.patient_id && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            👤 Patient:{' '}
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate(`/patients/${item.patient_id}`)}
              sx={{ textAlign: 'left' }}
            >
              {item.patient_name}
            </Link>
          </Typography>
        )}
        {item.sent_date && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            📤 Sent: {formatDate(item.sent_date)}
          </Typography>
        )}
        {item.start_date && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            ▶️ Started: {formatDate(item.start_date)}
          </Typography>
        )}
        {item.finish_date && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            ✅ Finished: {formatDate(item.finish_date)}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          📅 Created: {formatDateTime(item.created_at)}
        </Typography>
      </Box>
    </>
  );

  const filterActions = (
    <Box display="flex" gap={2} alignItems="center">
      {/* Questionnaire Type Filter */}
      <FormControl sx={{ minWidth: 200 }} size="small">
        <InputLabel>Type</InputLabel>
        <Select
          value={selectedTypeId || ''}
          onChange={(e) => setSelectedTypeId(e.target.value ? Number(e.target.value) : undefined)}
          label="Type"
        >
          <MenuItem value="">All Types</MenuItem>
          {questionnaireTypes.map((type) => (
            <MenuItem key={type.id} value={type.id}>
              {type.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Status Filter */}
      <FormControl sx={{ minWidth: 150 }} size="small">
        <InputLabel>Status</InputLabel>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          label="Status"
        >
          <MenuItem value="">All Statuses</MenuItem>
          <MenuItem value="SENT">Sent</MenuItem>
          <MenuItem value="START">Started</MenuItem>
          <MenuItem value="SUBMITTED">Submitted</MenuItem>
          <MenuItem value="FINISHED">Finished</MenuItem>
          <MenuItem value="EXIT">Exited</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );

  return (
    <TablePageLayout loading={table.loading} error={table.error} errorPrefix="Error loading questionnaires:">
      <DataTableToolbar
        {...table.toolbarProps}
        searchQuery={searchInputValue}
        onSearchChange={setSearchInputValue}
        onSearchSubmit={() => { table.setSearchQuery(searchInputValue); setCurrentPage(1); }}
        title="Questionnaires"
        searchPlaceholder="Search questionnaires..."
        isAdmin={!!user?.user_privilege?.config}
        actions={filterActions}
      />

      {isMobile ? (
        <MobileCardGrid
          data={table.sortedData}
          renderCard={renderMobileCard}
          onMenuClick={table.handleRowClick}
          getItemKey={(item) => item.id}
          emptyIcon={<AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
          emptyMessage="No questionnaires found"
        />
      ) : (
        <DataTableContainer {...table.containerProps}>
          <DataTableBody
            data={table.sortedData}
            {...table.tableBodyProps}
            getColumnValue={getColumnValue}
            onRowClick={table.handleRowClick}
            getRowKey={(item) => item.id}
          />
        </DataTableContainer>
      )}

      {showPagination && (
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPrevious={() => setCurrentPage(p => Math.max(1, p - 1))}
          onNext={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
        />
      )}

      <RowContextMenu {...table.menuProps} options={getContextMenuOptions()} />

      <ManageColumnsModal {...table.manageColumnsProps} />
      <SaveViewModal {...table.saveViewModalProps} isAdmin={!!user?.user_privilege?.config} />

      {/* Email Send Dialog */}
      {selectedForEmail && currentTenant && (
        <QuestionnaireEmailSendDialog
          open={emailDialogOpen}
          onClose={() => {
            setEmailDialogOpen(false);
            setSelectedForEmail(null);
          }}
          questionnaireId={selectedForEmail.id}
          questionnaireTypeName={selectedForEmail.questionnaire_type?.name || 'Unknown'}
          questionnaireTypeId={selectedForEmail.questionnaire_type_id || 0}
          answers={selectedForEmail.answers || {}}
          patientId={selectedForEmail.patient_id}
          tenantId={currentTenant.id}
          onSendSuccess={handleEmailSuccess}
          onSendError={handleEmailError}
        />
      )}

      {/* SMS Send Dialog */}
      {selectedForSms && currentTenant && (
        <QuestionnaireSMSSendDialog
          open={smsSendDialogOpen}
          onClose={() => {
            setSmsSendDialogOpen(false);
            setSelectedForSms(null);
          }}
          questionnaireId={selectedForSms.id}
          questionnaireTypeName={selectedForSms.questionnaire_type?.name || 'Unknown'}
          questionnaireTypeId={selectedForSms.questionnaire_type_id || 0}
          answers={selectedForSms.answers || {}}
          patientId={selectedForSms.patient_id}
          tenantId={currentTenant.id}
          onSendSuccess={(message) => toast.success(message)}
          onSendError={(error) => toast.error(error)}
        />
      )}

      {/* View Questionnaire Modal */}
      {selectedForView && currentTenant && (
        <ViewQuestionnaireModal
          isOpen={viewQuestionnaireModalOpen}
          onClose={() => {
            setViewQuestionnaireModalOpen(false);
            setSelectedForView(null);
          }}
          questionnaireId={selectedForView.id}
          questionnaireTitle={selectedForView.questionnaire_type?.title || 'Questionnaire'}
        />
      )}
    </TablePageLayout>
  );
};

export default QuestionnairesPage;
